import { useCallback, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import { param } from "~/utils/url_helper.jsx";

export const useEstimates = (config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/estimates`,
        signal,
      }),
  });
};

export const useArchivedEstimates = (config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates", "archive"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/estimates/archive`,
        signal,
      }),
  });
};

export const useUpdateEstimateMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/estimates/${id}`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["estimates"]);
      queryClient.invalidateQueries(["estimates-available-filters"]);
      return response;
    },
  });
};

export const useMoveToTrashEstimateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      fetch({
        url: `${Angie.api_url}/move-to-trash/estimate/${id}`,
        method: "PUT",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["estimates"]);
      queryClient.invalidateQueries(["estimates-available-filters"]);
      return response;
    },
  });
};

export const useCreateEstimateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/estimates`,
        method: "POST",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["estimates"], { exact: true });
      queryClient.invalidateQueries(["estimates-available-filters"]);
      return response;
    },
  });
};

export const useSendEstimateMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/estimates/${id}/send`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["estimates"]);
      queryClient.invalidateQueries(["estimates-available-filters"]);
      return response;
    },
  });
};

export const useEstimateMarkAsSentMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/estimates/${id}/mark-as-sent`,
        method: "POST",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useEstimate = (id, config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates", id],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/estimates/${id}`,
      }),
  });
};

export const useEstimatesForScreen = (queryParams, config) => {
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 100,
    total: 0,
  });

  const fetchData = useCallback(
    ({ pageParam = 1, signal }) => {
      const params = {
        page: pageParam,
        ...queryParams,
      };
      return fetch({
        url: `${Angie.api_url}/estimates/for-screen?${param(params)}`,
        signal,
        withHeaders: true,
      }).then((response) => {
        const _pagination = {
          page: parseInt(response._headers["x-angie-paginationcurrentpage"], 10),
          perPage: parseInt(response._headers["x-angie-paginationitemsperpage"], 10),
          total: parseInt(response._headers["x-angie-paginationtotalitems"], 10),
        };
        setPagination(_pagination);
        return response._parsedBody;
      });
    },
    [queryParams]
  );

  return useInfiniteQuery({
    ...config,
    queryKey: ["estimates", "for-screen", queryParams],
    queryFn: fetchData,
    getNextPageParam: () => {
      const totalPages = Math.ceil(pagination.total / pagination.perPage);
      if (pagination.page < totalPages) {
        return pagination.page + 1;
      }
      return undefined;
    },
  });
};

export const useAvailableEstimatesFilters = (config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates-available-filters"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/estimates/available-filters`,
        signal,
      }),
  });
};

export const useEstimateSuggestNumber = (config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates", "suggest-number"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/estimates/suggest-number`,
        signal,
      }),
  });
};

export const useEstimateInvoices = (id, config) => {
  return useQuery({
    ...config,
    queryKey: ["estimates", "invoices", id],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/estimates/${id}/invoices`,
        signal,
      }),
  });
};
